import { useCallback, useMemo } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { Creator } from '~/clientApi/creator.model';
import { SOCIAL_PLATFORMS } from '~/constants';
import { ContentsApiResponseResultsType } from '~/searchApi';

import { TalentChartProps } from '../blocks/TalentCharts/TalentCharts';
import { TalentContentProperties } from '../blocks/TalentContent/TalentContent';
import { usePageBuilderStore } from '../hooks/usePageBuilderStore';
import { BrandLogosProperties } from '../modals/BrandExperienceModal';
import { useBrandExperienceModalStore } from '../modals/BrandExperienceModal/useBrandExperienceModalStore';
import { usePlatformAnalyticsStore } from '../modals/PlatformAnalytics/usePlatformAnalyticsStore';
import {
  VideoUrlModalStore,
  useVideoUrlModalStore,
} from '../modals/VideoUrlModal/useVideoUrlModalStore';
import { AnyBlock, Block, BlockType } from '../types';
import { isEditorEmpty } from '../utils/editorUtils';
import { useAddContentStore } from './useAddContentStore';
import {
  SelectCreatorStore,
  useSelectCreatorStore,
} from './useSelectCreatorStore';

const mapMetricsFallback = (metrics: string[]) => {
  return metrics?.map((metric) => {
    if (metric === 'Average Impressions') {
      return 'Average Views';
    }
    if (metric === 'Average Story Impressions') {
      return 'Average Story Views';
    }
    if (metric === 'Average Reels Impressions') {
      return 'Average Reels Views';
    }
    return metric;
  });
};

export const useDropActions = () => {
  const {
    addBlock,
    updateBlock,
    blocks,
    setEditingBlockId,
    removeBlock,
    editingBlockId,
  } = usePageBuilderStore();
  const { setIsModalOpen } = useSelectCreatorStore();
  const { setIsModalOpen: openContentModal, setIsEditMode } =
    useAddContentStore();
  const { setIsModalOpen: setAnalyticsModalOpen } = usePlatformAnalyticsStore();
  const { setIsOpen: setIsVideoModalOpen } = useVideoUrlModalStore();
  const { setIsOpen: setIsBrandExperienceModalOpen } =
    useBrandExperienceModalStore();

  const creatorsFromBlocks = useMemo(() => {
    if (!blocks.length) return [];

    return (
      blocks
        .filter((b: AnyBlock) => b.component === 'TalentProfile')
        // @ts-ignore - somehow the version of ts in this project doesn't recognize this prop
        .map((b) => b.properties.creator)
    );
  }, [blocks]);

  const waitForCreatorSelection = useCallback(
    (mode: SelectCreatorStore['mode']) =>
      new Promise<Creator>((resolve) => {
        const callback = (creator: Creator) => {
          if (creator) resolve(creator);
          setIsModalOpen(false);
        };
        setIsModalOpen(true, mode, callback);
      }),
    [setIsModalOpen],
  );

  const waitForContentSelection = useCallback(
    (id: string, isEdit: boolean, blockId: string) =>
      new Promise<{
        content: ContentsApiResponseResultsType[];
        showInShared: boolean;
      }>((resolve) => {
        const callback = (
          content: ContentsApiResponseResultsType[],
          showInShared?: boolean,
        ) => {
          if (content)
            resolve({ content, showInShared: showInShared ?? false });
          openContentModal(false, undefined);
          if (isEdit) setIsEditMode(false, undefined);
        };
        openContentModal(true, id, callback);
        if (isEdit) setIsEditMode(true, blockId);
      }),
    [openContentModal, setIsEditMode],
  );

  const waitForDemographicsMetricsSelection = useCallback(
    (initialSelections?: {
      demographics?: TalentChartProps['demographics'];
      metrics?: TalentChartProps['metrics'];
    }) =>
      new Promise<{
        demographics: TalentChartProps['demographics'];
        metrics: TalentChartProps['metrics'];
      }>((resolve) => {
        const callback = (
          demographics: TalentChartProps['demographics'],
          metrics: TalentChartProps['metrics'],
        ) => {
          if (demographics || metrics) {
            resolve({
              demographics,
              metrics,
            });
          }
          setAnalyticsModalOpen(false);
        };
        setAnalyticsModalOpen(true, callback, {
          selectedDemographics: initialSelections?.demographics,
          selectedMetrics: initialSelections?.metrics,
        });
      }),
    [setAnalyticsModalOpen],
  );

  const waitForVideoUrlInput = useCallback(
    (
      mode: VideoUrlModalStore['mode'],
      initialUrl?: VideoUrlModalStore['initialUrl'],
    ) =>
      new Promise<string>((resolve) => {
        const callback = (url: string) => {
          if (url) {
            resolve(url);
          }
          setIsVideoModalOpen(false);
        };
        setIsVideoModalOpen(true, { callback, initialUrl, mode });
      }),
    [setIsVideoModalOpen],
  );

  const waitForBrandExperienceSelection = useCallback(
    () =>
      new Promise<BrandLogosProperties>((resolve) => {
        const callback = (logos: BrandLogosProperties) => {
          if (logos && logos.logos) resolve(logos);
          setIsBrandExperienceModalOpen(false);
        };
        setIsBrandExperienceModalOpen(true, { callback });
      }),
    [setIsBrandExperienceModalOpen],
  );

  const trigger = useCallback(
    async (type: BlockType, metadata: any = {}) => {
      // Helper function to scroll block into view
      const scrollBlockIntoView = (blockId: string) => {
        setTimeout(() => {
          const element = document.querySelector(
            `[data-block-id="${blockId}"]`,
          );
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100); // Small delay to ensure DOM is updated
      };

      if (type === 'TalentProfile') {
        const creator = (await waitForCreatorSelection('all')) as Creator;
        const blockId = uuidV4();

        addBlock({
          id: blockId,
          component: type,
          properties: {
            creatorId: creator.id,
            creator: creator,
          },
        });

        scrollBlockIntoView(blockId);
      }

      if (type === 'TalentContent') {
        const { id } = (await waitForCreatorSelection(
          'from-blocks',
        )) as Creator;

        let customContent: TalentContentProperties['customContent'];
        let contentShowInShared: boolean;
        const result = (await waitForContentSelection(
          id,
          metadata?.isEdit,
          metadata?.blockId,
        )) as {
          content: TalentContentProperties['customContent'];
          showInShared: boolean;
        };
        customContent = result.content;
        contentShowInShared = result.showInShared;

        const block = blocks.find(
          (block: AnyBlock) =>
            block.component === 'TalentContent' &&
            block.id === metadata?.blockId,
        ) as Block<'TalentContent'> | undefined;

        if (block && metadata?.isEdit) {
          if (customContent.length === 0) {
            removeBlock(block.id);
            return;
          }
          updateBlock(block?.id, {
            component: block.component,
            id: block.id,
            properties: {
              creatorId: id,
              customContent: customContent,
              showInShared: contentShowInShared,
            },
          });
          scrollBlockIntoView(block.id);
          return;
        }

        const blockId = uuidV4();

        addBlock({
          id: blockId,
          component: type,
          properties: {
            creatorId: id,
            customContent,
            showInShared: contentShowInShared,
          },
        });
        scrollBlockIntoView(blockId);
      }

      if (type === 'TalentCharts') {
        let creatorId: string;

        if (creatorsFromBlocks.length === 1) {
          // Only one creator - select him/her (Hope I don't get cancelled for mentioning two genders smh)
          creatorId = creatorsFromBlocks.at(0)?.id as string;
        } else {
          // Open modal to select creator (likely to be useful for v2)
          const { id } = (await waitForCreatorSelection(
            'from-blocks',
          )) as Creator;
          creatorId = id;
        }

        const block = blocks.find(
          (block: AnyBlock) =>
            block.component === 'TalentCharts' &&
            block.properties.platform === metadata.platform,
        ) as Block<'TalentCharts'> | undefined;

        const { demographics, metrics } =
          (await waitForDemographicsMetricsSelection({
            demographics:
              metadata?.demographics ?? block?.properties?.demographics,
            metrics: mapMetricsFallback(
              metadata?.metrics ?? block?.properties?.metrics,
            ),
          })) as {
            demographics: TalentChartProps['demographics'];
            metrics: TalentChartProps['metrics'];
          };

        if (block) {
          updateBlock(block?.id, {
            component: block.component,
            id: block.id,
            properties: {
              platform: metadata.platform as SOCIAL_PLATFORMS,
              creatorId: creatorId,
              demographics: demographics,
              metrics: metrics,
            },
          });
          scrollBlockIntoView(block.id);
          return;
        }

        const blockId = uuidV4();
        addBlock({
          id: blockId,
          component: type,
          properties: {
            platform: metadata.platform as SOCIAL_PLATFORMS,
            creatorId: creatorId,
            demographics: demographics,
            metrics: metrics,
          },
        });
        scrollBlockIntoView(blockId);
      }

      if (type === 'EditableRichTextView') {
        const blockId = uuidV4();

        // First, exit edit mode for any existing blocks
        if (editingBlockId) {
          const existingBlock = blocks.find((b) => b.id === editingBlockId) as
            | Block<'EditableRichTextView'>
            | undefined;
          if (
            existingBlock &&
            isEditorEmpty(JSON.parse(existingBlock.properties.editorState))
          ) {
            removeBlock(editingBlockId);
          } else {
            setEditingBlockId(null);
          }
        }

        // Then add the new block
        addBlock({
          id: blockId,
          component: type,
          properties: {
            editorState: '',
          },
        });
        setEditingBlockId(blockId);
        scrollBlockIntoView(blockId);
      }

      if (type === 'TalentVideo') {
        const blockId = uuidV4();
        const oldUrl = metadata?.url;

        const mode = metadata?.isEdit ? 'edit' : 'add';
        const newUrl = await waitForVideoUrlInput(mode, oldUrl);

        if (metadata?.isEdit && metadata?.blockId) {
          const block = blocks.find(
            (block: AnyBlock) =>
              block.component === 'TalentVideo' &&
              block.id === metadata.blockId,
          ) as Block<'TalentVideo'> | undefined;
          if (block) {
            // Edit block
            updateBlock(block.id, {
              component: block.component,
              id: block.id,
              properties: {
                url: newUrl as string,
              },
            });
            return;
          }
        }
        addBlock({
          id: blockId,
          component: type,
          properties: {
            url: newUrl as string,
          },
        });
        scrollBlockIntoView(blockId);
        return;
      }

      if (type === 'BrandLogos') {
        const logosData =
          (await waitForBrandExperienceSelection()) as BrandLogosProperties;

        const brandLogosBlock = blocks.find(
          (block: AnyBlock) => block.component === 'BrandLogos',
        ) as Block<'BrandLogos'> | undefined;

        if (brandLogosBlock && logosData.logos.length === 0) {
          removeBlock(brandLogosBlock?.id);
          return;
        }

        if (brandLogosBlock && logosData.logos.length > 0) {
          updateBlock(brandLogosBlock.id, {
            id: brandLogosBlock.id,
            component: type,
            properties: {
              logos: logosData.logos,
            },
          });
          return;
        }

        const blockId = uuidV4();

        addBlock({
          id: blockId,
          component: type,
          properties: logosData,
        });

        scrollBlockIntoView(blockId);

        return;
      }
    },
    [
      waitForCreatorSelection,
      addBlock,
      waitForContentSelection,
      creatorsFromBlocks,
      blocks,
      waitForDemographicsMetricsSelection,
      waitForBrandExperienceSelection,
      updateBlock,
      editingBlockId,
      setEditingBlockId,
      removeBlock,
    ],
  );

  return trigger;
};
